import { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  ExternalLink,
  Info,
  Route,
  ShieldAlert,
  Snowflake,
  TestTubes,
} from "lucide-react";

import type {
  BioanalysisPhaseTwo as BioanalysisPhaseTwoModel,
  SampleEvidenceStatus,
} from "../lib/bioanalysisPhaseTwo";
import { cn } from "../lib/utils";

type PhaseTwoTab = "conditions" | "workflow" | "stability" | "risks";

interface BioanalysisPhaseTwoProps {
  analysis: BioanalysisPhaseTwoModel;
}

const READINESS_LABELS: Record<BioanalysisPhaseTwoModel["readiness"], { label: string; className: string }> = {
  ready: { label: "可形成管理草案", className: "text-success border-success/30 bg-success/5" },
  partial: { label: "需补充执行条件", className: "text-warning border-warning/30 bg-warning/5" },
  insufficient: { label: "仅可用于核查", className: "text-destructive border-destructive/30 bg-destructive/5" },
};

const STATUS_LABELS: Record<SampleEvidenceStatus, { label: string; className: string }> = {
  supported: { label: "已有证据", className: "bg-success/10 text-success" },
  planned: { label: "文件控制", className: "bg-primary/10 text-primary" },
  gap: { label: "待确认", className: "bg-warning/10 text-warning" },
};

function StatusBadge({ status }: { status: SampleEvidenceStatus }) {
  const item = STATUS_LABELS[status];
  return <span className={cn("inline-flex rounded-md px-2 py-1 text-xs font-semibold", item.className)}>{item.label}</span>;
}

function EvidenceLink({ href }: { href: string }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
    >
      核对来源
      <ExternalLink className="h-3 w-3" aria-hidden="true" />
    </a>
  );
}

function ConditionsTable({ analysis }: BioanalysisPhaseTwoProps) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table aria-label="样本处理条件证据表" className="min-w-[64rem] w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
            <th className="w-14 px-3 py-2.5 text-left font-medium">序号</th>
            <th className="w-48 px-3 py-2.5 text-left font-medium">关键条件</th>
            <th className="min-w-[20rem] px-3 py-2.5 text-left font-medium">当前值</th>
            <th className="min-w-[18rem] px-3 py-2.5 text-left font-medium">证据依据</th>
            <th className="w-28 px-3 py-2.5 text-left font-medium">状态</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {analysis.conditions.map((item, index) => (
            <tr key={item.id} className="align-top hover:bg-muted/20">
              <td className="px-3 py-3 tabular-nums text-muted-foreground">{index + 1}</td>
              <td className="px-3 py-3 font-semibold text-foreground">{item.label}</td>
              <td className={cn("px-3 py-3 leading-relaxed", item.status === "gap" ? "text-muted-foreground" : "text-foreground")}>{item.value}</td>
              <td className="px-3 py-3">
                <div className="leading-relaxed text-muted-foreground">{item.basis}</div>
                <div className="mt-1.5"><EvidenceLink href={item.sourceUrl} /></div>
              </td>
              <td className="px-3 py-3"><StatusBadge status={item.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WorkflowTable({ analysis }: BioanalysisPhaseTwoProps) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table aria-label="临床样本全流程控制表" className="min-w-[86rem] w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
            <th className="w-14 px-3 py-2.5 text-left font-medium">序号</th>
            <th className="w-36 px-3 py-2.5 text-left font-medium">环节</th>
            <th className="min-w-[20rem] px-3 py-2.5 text-left font-medium">关键控制点</th>
            <th className="min-w-[22rem] px-3 py-2.5 text-left font-medium">当前证据</th>
            <th className="min-w-[24rem] px-3 py-2.5 text-left font-medium">方案 / 手册应明确</th>
            <th className="min-w-[20rem] px-3 py-2.5 text-left font-medium">必需记录</th>
            <th className="w-28 px-3 py-2.5 text-left font-medium">状态</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {analysis.workflow.map((item, index) => (
            <tr key={item.id} className="align-top hover:bg-muted/20">
              <td className="px-3 py-3 tabular-nums text-muted-foreground">{index + 1}</td>
              <td className="px-3 py-3 font-semibold text-foreground">{item.stage}</td>
              <td className="px-3 py-3 leading-relaxed text-foreground">{item.controlPoint}</td>
              <td className="px-3 py-3 leading-relaxed text-muted-foreground">{item.currentEvidence}</td>
              <td className="px-3 py-3 leading-relaxed text-foreground">{item.protocolDecision}</td>
              <td className="px-3 py-3 leading-relaxed text-muted-foreground">{item.requiredRecord}</td>
              <td className="px-3 py-3"><StatusBadge status={item.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StabilityTable({ analysis }: BioanalysisPhaseTwoProps) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table aria-label="样本稳定性覆盖矩阵" className="min-w-[76rem] w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
            <th className="w-14 px-3 py-2.5 text-left font-medium">序号</th>
            <th className="w-56 px-3 py-2.5 text-left font-medium">稳定性项目</th>
            <th className="min-w-[22rem] px-3 py-2.5 text-left font-medium">应覆盖的研究场景</th>
            <th className="min-w-[22rem] px-3 py-2.5 text-left font-medium">当前证据</th>
            <th className="min-w-[22rem] px-3 py-2.5 text-left font-medium">研究决策</th>
            <th className="w-44 px-3 py-2.5 text-left font-medium">法规依据</th>
            <th className="w-28 px-3 py-2.5 text-left font-medium">状态</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {analysis.stability.map((item, index) => (
            <tr key={item.id} className="align-top hover:bg-muted/20">
              <td className="px-3 py-3 tabular-nums text-muted-foreground">{index + 1}</td>
              <td className="px-3 py-3 font-semibold text-foreground">{item.study}</td>
              <td className="px-3 py-3 leading-relaxed text-foreground">{item.scenario}</td>
              <td className="px-3 py-3 leading-relaxed text-muted-foreground">{item.currentEvidence}</td>
              <td className="px-3 py-3 leading-relaxed text-foreground">{item.protocolDecision}</td>
              <td className="px-3 py-3"><a href={item.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">{item.sourceTitle}<ExternalLink className="h-3 w-3" aria-hidden="true" /></a></td>
              <td className="px-3 py-3"><StatusBadge status={item.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RisksTable({ analysis }: BioanalysisPhaseTwoProps) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table aria-label="样本偏差与风险控制表" className="min-w-[78rem] w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
            <th className="w-14 px-3 py-2.5 text-left font-medium">序号</th>
            <th className="min-w-[19rem] px-3 py-2.5 text-left font-medium">风险事件</th>
            <th className="min-w-[22rem] px-3 py-2.5 text-left font-medium">对研究的影响</th>
            <th className="min-w-[25rem] px-3 py-2.5 text-left font-medium">预防控制</th>
            <th className="min-w-[24rem] px-3 py-2.5 text-left font-medium">偏差评估至少记录</th>
            <th className="w-24 px-3 py-2.5 text-left font-medium">等级</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {analysis.risks.map((item, index) => (
            <tr key={item.id} className="align-top hover:bg-muted/20">
              <td className="px-3 py-3 tabular-nums text-muted-foreground">{index + 1}</td>
              <td className="px-3 py-3 font-semibold text-foreground">{item.risk}</td>
              <td className="px-3 py-3 leading-relaxed text-foreground">{item.impact}</td>
              <td className="px-3 py-3 leading-relaxed text-muted-foreground">{item.preventiveControl}</td>
              <td className="px-3 py-3 leading-relaxed text-muted-foreground">{item.deviationRecord}</td>
              <td className="px-3 py-3"><span className={cn("inline-flex rounded-md px-2 py-1 text-xs font-semibold", item.severity === "critical" ? "bg-destructive/10 text-destructive" : "bg-warning/10 text-warning")}>{item.severity === "critical" ? "关键" : "主要"}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BioanalysisPhaseTwo({ analysis }: BioanalysisPhaseTwoProps) {
  const [activeTab, setActiveTab] = useState<PhaseTwoTab>("conditions");
  const readiness = READINESS_LABELS[analysis.readiness];
  const supportedConditions = analysis.conditions.filter((item) => item.status === "supported").length;
  const supportedStability = analysis.stability.filter((item) => item.status === "supported").length;
  const tabs = useMemo(() => [
    { id: "conditions" as const, label: "样本条件", count: analysis.conditions.length, icon: TestTubes },
    { id: "workflow" as const, label: "全流程控制", count: analysis.workflow.length, icon: Route },
    { id: "stability" as const, label: "稳定性覆盖", count: analysis.stability.length, icon: Snowflake },
    { id: "risks" as const, label: "偏差与风险", count: analysis.risks.length, icon: ShieldAlert },
  ], [analysis.conditions.length, analysis.workflow.length, analysis.stability.length, analysis.risks.length]);

  return (
    <section className="card overflow-hidden" aria-labelledby="bioanalysis-phase-two-title">
      <div className="border-b border-border px-5 py-4 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-primary" aria-hidden="true" />
              <h2 id="bioanalysis-phase-two-title" className="text-base font-semibold text-foreground">
                临床样本全流程管理 · 第二阶段
              </h2>
            </div>
            <p className="mt-1.5 max-w-4xl text-sm leading-relaxed text-muted-foreground">{analysis.summary}</p>
          </div>
          <div className={cn("flex flex-shrink-0 items-center gap-2 rounded-md border px-3 py-2", readiness.className)}>
            {analysis.readiness === "ready" ? <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> : <AlertTriangle className="h-4 w-4" aria-hidden="true" />}
            <span className="text-sm font-semibold">{readiness.label}</span>
            <span className="border-l border-current/20 pl-2 text-sm font-bold tabular-nums">{analysis.score}/100</span>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 border-t border-border/70 pt-3 text-xs text-muted-foreground">
          <span>明确样本条件 <strong className="text-foreground tabular-nums">{supportedConditions}/{analysis.conditions.length}</strong></span>
          <span>已识别稳定性证据 <strong className="text-foreground tabular-nums">{supportedStability}/{analysis.stability.length}</strong></span>
          <span>关键风险控制 <strong className="text-foreground tabular-nums">{analysis.risks.length}</strong></span>
        </div>
      </div>

      <div className="px-5 py-4 sm:px-6">
        <div className="inline-flex max-w-full overflow-x-auto rounded-md border border-border bg-muted/30 p-1" role="tablist" aria-label="临床样本全流程管理视图">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const selected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setActiveTab(tab.id)}
                className={cn("inline-flex min-h-9 items-center gap-2 whitespace-nowrap rounded px-3 text-sm font-medium transition-colors", selected ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {tab.label}
                <span className="rounded bg-muted px-1.5 py-0.5 text-[0.68rem] tabular-nums">{tab.count}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-4" role="tabpanel">
          {activeTab === "conditions" && <ConditionsTable analysis={analysis} />}
          {activeTab === "workflow" && <WorkflowTable analysis={analysis} />}
          {activeTab === "stability" && <StabilityTable analysis={analysis} />}
          {activeTab === "risks" && <RisksTable analysis={analysis} />}
        </div>

        {analysis.gaps.length > 0 && (
          <div className="mt-4 border-t border-border pt-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground"><AlertTriangle className="h-4 w-4 text-warning" aria-hidden="true" />方案定稿前的证据缺口</div>
            <ul className="mt-2 grid gap-2 text-sm leading-relaxed text-muted-foreground md:grid-cols-2">
              {analysis.gaps.slice(0, 10).map((gap) => <li key={gap} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-warning" aria-hidden="true" /><span>{gap}</span></li>)}
            </ul>
          </div>
        )}

        <div className="mt-4 flex items-start gap-2 border-t border-border pt-3 text-xs leading-relaxed text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{analysis.limitations.join(" ")}</span>
        </div>
      </div>
    </section>
  );
}
